import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getWorkoutLogPreferences,
  patchWorkoutPreferences,
} from "../api/workoutLogApi";
import { resolveWorkoutPreferences } from "../domain/workoutPreferenceDefaults";
import { workoutLogStore } from "../store/localExecutionStore";

export default function useWorkoutPreferences(userId, {
  online = true,
  store = workoutLogStore,
  getPreferencesApi = getWorkoutLogPreferences,
  patchPreferencesApi = patchWorkoutPreferences,
} = {}) {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(Boolean(userId));
  // Queued first-use answers this consumer already sees through the existing
  // local read (its mount-time cache read, its own queued answer). While the
  // store still holds a pending patch they stay on top of a confirmed
  // publication, so a confirmed save never re-opens a first-use sheet; queued
  // values are never learnt from a publication (Phase 6E.1).
  const localPendingRef = useRef(null);

  useEffect(() => {
    let active = true;
    localPendingRef.current = null;
    if (!userId) {
      setPreferences(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    // Phase 6E: a confirmed publication for this user (a Workout Settings
    // save, a sync acknowledgement) reaches this mounted consumer at once.
    // `published` only saves a redundant write; the store's conditional write
    // below is what keeps an older GET from overriding a newer publication.
    let published = false;
    const unsubscribe = store.subscribePreferences?.(userId, (publication) => {
      if (!active || !publication?.preferences) return;
      published = true;
      if (!publication.hasPendingPatch) localPendingRef.current = null;
      setPreferences({ ...publication.preferences, ...(localPendingRef.current || {}) });
    });
    (async () => {
      const cached = await store.getPreferences(userId).catch(() => null);
      // The token this GET's cache write is conditional on (Phase 6E.1).
      const changeToken = Number(cached?.localChangeToken) || 0;
      if (active && cached?.pendingPatch) {
        localPendingRef.current = { ...cached.pendingPatch, ...(localPendingRef.current || {}) };
        if (published) setPreferences((current) => ({ ...current, ...localPendingRef.current }));
      }
      if (active && !published && cached?.preferences) setPreferences(cached.preferences);
      if (online) {
        try {
          const remote = await getPreferencesApi();
          if (remote && !published) {
            // Skipped (null) when any preference write committed since the
            // snapshot read: the compare and write share one transaction.
            const stored = await store.cachePreferences(userId, remote, { ifChangeToken: changeToken });
            if (active && stored) setPreferences(stored.preferences);
          }
        } catch (_error) {
          // A cached preference remains usable. Missing values are never
          // invented; the first-use sheet stays open until the athlete chooses.
        }
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [getPreferencesApi, online, store, userId]);

  const answer = useCallback(async (patch) => {
    if (!userId) throw new Error("A signed-in user is required.");
    if (online) {
      try {
        const remote = await patchPreferencesApi(patch);
        const cached = await store.cachePreferences(userId, remote);
        setPreferences(cached.preferences);
        return { preferences: cached.preferences, pending: false };
      } catch (_error) {
        // The explicit choice is still valid for this execution. Queue it for
        // the Phase 4 preference flusher rather than silently choosing another.
      }
    }
    const cached = await store.queuePreferencesPatch(userId, patch);
    localPendingRef.current = { ...(localPendingRef.current || {}), ...patch };
    setPreferences(cached.preferences);
    return { preferences: cached.preferences, pending: true };
  }, [online, patchPreferencesApi, store, userId]);

  // `preferences` stays the raw known value (null while unavailable) so the
  // first-use prompts still see unanswered values; `resolvedPreferences` fills
  // the canonical defaults for rest mode, default rest and Keep Screen Awake.
  const resolvedPreferences = useMemo(() => resolveWorkoutPreferences(preferences), [preferences]);

  return { preferences, resolvedPreferences, loading, answer };
}
